// v231 admin page for the Pull-Gate-Policy-Engine (item 47 of
// PRODUCT-ROADMAP.md, "Pull-Gate-Policy-Engine — Image-Firewall für
// Pulls"; item 53 in the recommended delivery order, Phase B).
//
// Phase A landed migration 083 + the in-process engine + the
// formatutil hook with `enabled=FALSE` as the safe default. This page
// is the operator surface that Phase B promised:
//
//   1. Master-toggle card    — enable / sync_mode / grace / fallback.
//   2. Live-preview card     — runs the engine against the most-recent
//                              200 artifacts in *simulate* mode so an
//                              admin sees "if I flipped this on right
//                              now, X% of the next pulls would block,
//                              Y% would bypass, Z% would allow"
//                              before they actually flip the switch.
//   3. Policies card         — table + create/edit form. Scopes layer
//                              global → project → repo; the engine
//                              fails closed so any layer can block.
//   4. Audit-log card        — tail of recent decisions, filterable by
//                              action chip (all / allow / block /
//                              bypass).
//
// The big-toggle UX deliberately mirrors AdminMaintenance — flipping
// a master switch ON while the policies are misconfigured can hard-
// stop every download, so the warning banner above the toggle is
// loud-on-purpose.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Edit,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Shield,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  usePullGateSettings,
  usePullGatePolicies,
  usePullGateAudit,
  usePullGatePreview,
  usePullGateStats,
  updatePullGateSettings,
  createPullGatePolicy,
  updatePullGatePolicy,
  deletePullGatePolicy,
  PULL_GATE_STREAM_URL,
  type PullGateSettings,
  type PullGatePolicy,
  type PullGatePolicyInput,
  type PullGateAction,
  type PullGateAuditRow,
  type PullGateLiveEvent,
  type PullGatePreview,
  type PullGateStats,
  type PolicyScope,
  type SyncMode,
} from '../../api/pullGate';

const LABEL_CLASS = 'text-sm text-fg-muted block mb-1.5';

function emptyPolicyDraft(): PullGatePolicyInput {
  return {
    scope_kind: 'global',
    scope_id: '',
    name: '',
    enabled: true,
    require_signed: false,
    max_severity: '',
    max_age_days: 0,
    require_sbom: false,
    allowed_licenses: [],
    forbidden_licenses: [],
    bypass_patterns: [],
    block_message_template: '',
  };
}

function policyToDraft(p: PullGatePolicy): PullGatePolicyInput {
  return {
    scope_kind: p.scope_kind,
    scope_id: p.scope_id ?? '',
    name: p.name,
    enabled: p.enabled,
    require_signed: p.require_signed,
    max_severity: p.max_severity ?? '',
    max_age_days: p.max_age_days ?? 0,
    require_sbom: p.require_sbom,
    allowed_licenses: p.allowed_licenses ?? [],
    forbidden_licenses: p.forbidden_licenses ?? [],
    bypass_patterns: p.bypass_patterns ?? [],
    block_message_template: p.block_message_template ?? '',
  };
}

function fmtRel(d?: string): string {
  if (!d) return '—';
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  if (Number.isNaN(diff)) return d;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function csv(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function decisionBadgeTone(
  decision: PullGateAction,
): 'danger' | 'warning' | 'success' {
  if (decision === 'block') return 'danger';
  if (decision === 'bypass') return 'warning';
  return 'success';
}

function scopeBadgeTone(
  scope: PolicyScope,
): 'accent' | 'nebula' | 'neutral' {
  if (scope === 'repo') return 'accent';
  if (scope === 'project') return 'nebula';
  return 'neutral';
}

function streamStatusTone(
  connected: boolean,
  error: string | null,
): 'success' | 'danger' | 'neutral' {
  if (connected) return 'success';
  if (error) return 'danger';
  return 'neutral';
}

function streamStatusLabel(
  connected: boolean,
  error: string | null,
  running: boolean,
): string {
  if (connected) return 'connected';
  if (error) return 'error';
  if (running) return 'connecting…';
  return 'paused';
}

const SYNC_MODE_LABELS: Record<SyncMode, { label: string; hint: string }> = {
  'pre-scan-cache': {
    label: 'Pre-scan cache (recommended)',
    hint: 'Serve from cached scan_findings; missing scans drive the fallback action.',
  },
  'block-until-scan': {
    label: 'Block until scan',
    hint: 'Strictest. Missing scans always block — no fallback, no grace.',
  },
  'optimistic-with-quarantine': {
    label: 'Optimistic with quarantine',
    hint: 'First pull allowed; subsequent pulls re-evaluate via auto-quarantine.',
  },
};

export function AdminPullGate() {
  const settings = usePullGateSettings();
  const policies = usePullGatePolicies();
  const [auditFilter, setAuditFilter] = useState<PullGateAction | undefined>(
    undefined,
  );
  const audit = usePullGateAudit(auditFilter);
  const preview = usePullGatePreview(200);
  const stats = usePullGateStats();
  const stream = useDecisionStream();

  const [draft, setDraft] = useState<PullGateSettings | null>(null);
  const [policyDraft, setPolicyDraft] =
    useState<PullGatePolicyInput>(emptyPolicyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Hydrate the settings draft once the SWR fetch lands.
  useEffect(() => {
    if (settings.data && !draft) {
      setDraft(settings.data);
    }
  }, [settings.data, draft]);

  const policyRows = policies.data?.policies ?? [];
  const auditRows = audit.data?.rows ?? [];

  const counts = useMemo(() => {
    const n = { repo: 0, project: 0, global: 0, total: policyRows.length };
    for (const p of policyRows) {
      n[p.scope_kind] += 1;
    }
    return n;
  }, [policyRows]);

  async function saveSettings() {
    if (!draft) return;
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      await updatePullGateSettings({
        enabled: draft.enabled,
        sync_mode: draft.sync_mode,
        grace_period_hours: draft.grace_period_hours,
        fallback_action: draft.fallback_action,
      });
      setOk('Settings saved.');
      await Promise.all([settings.mutate(), preview.mutate()]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function refreshPreview() {
    setBusy(true);
    setErr(null);
    try {
      await preview.mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function loadPolicy(p: PullGatePolicy) {
    setPolicyDraft(policyToDraft(p));
    setEditingId(p.id);
    setErr(null);
  }

  function resetPolicyForm() {
    setPolicyDraft(emptyPolicyDraft());
    setEditingId(null);
    setErr(null);
  }

  async function submitPolicy() {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      const payload: PullGatePolicyInput = {
        ...policyDraft,
        scope_id:
          policyDraft.scope_kind === 'global'
            ? ''
            : (policyDraft.scope_id ?? '').trim(),
        max_severity: policyDraft.max_severity || '',
        max_age_days: policyDraft.max_age_days || 0,
      };
      if (editingId) {
        await updatePullGatePolicy(editingId, payload);
        setOk('Policy updated.');
      } else {
        await createPullGatePolicy(payload);
        setOk('Policy created.');
      }
      await policies.mutate();
      resetPolicyForm();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function togglePolicyEnabled(p: PullGatePolicy) {
    setBusy(true);
    setErr(null);
    try {
      await updatePullGatePolicy(p.id, {
        ...policyToDraft(p),
        enabled: !p.enabled,
      });
      await policies.mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removePolicy(p: PullGatePolicy) {
    if (
      !confirm(
        `Delete pull-gate policy "${p.name}"? This cannot be undone.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await deletePullGatePolicy(p.id);
      if (editingId === p.id) resetPolicyForm();
      await policies.mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-nebula" />
          Pull-Gate Policy Engine
        </span>
      }
      description="Synchronous policy gate evaluated in front of every artifact serve. Disabled by default — turn it on once your policies are validated against the live preview below."
    >
      {(err || ok) && (
        <Card className={err ? 'mb-4 border-danger/40' : 'mb-4 border-success/40'}>
          <CardBody>
            <p className={err ? 'text-sm text-danger' : 'text-sm text-success'}>
              {err || ok}
            </p>
          </CardBody>
        </Card>
      )}

      <PullGateMasterToggleCard
        draft={draft}
        setDraft={setDraft}
        saveSettings={saveSettings}
        busy={busy}
      />

      <PullGateLivePreviewCard
        preview={preview}
        refreshPreview={refreshPreview}
        busy={busy}
      />

      <PullGateStatsCard stats={stats} />

      <PullGateLiveStreamCard stream={stream} />

      <PullGatePoliciesCard
        counts={counts}
        editingId={editingId}
        resetPolicyForm={resetPolicyForm}
        policyDraft={policyDraft}
        setPolicyDraft={setPolicyDraft}
        submitPolicy={submitPolicy}
        busy={busy}
        policyRows={policyRows}
        loadPolicy={loadPolicy}
        removePolicy={removePolicy}
        togglePolicyEnabled={togglePolicyEnabled}
      />

      <PullGateAuditLogCard
        auditRows={auditRows}
        auditFilter={auditFilter}
        setAuditFilter={setAuditFilter}
      />

      {/* Footer hint — phase A coverage note */}
      <p className="text-xs text-fg-subtle mt-4 flex items-center gap-1.5">
        <Shield className="h-3.5 w-3.5" />
        The <code>require_signed</code> flag is acknowledged in the policy
        schema but treated as a no-op pending the per-artifact signing-
        verifications surface. Bypass patterns and license / severity / age /
        SBOM rules all evaluate today.
      </p>
    </AdminShell>
  );
}

function PullGateMasterToggleCard({
  draft,
  setDraft,
  saveSettings,
  busy,
}: Readonly<{
  draft: PullGateSettings | null;
  setDraft: (s: PullGateSettings) => void;
  saveSettings: () => void;
  busy: boolean;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Master toggle"
        description="Enabling the gate inserts a synchronous policy check on every artifact serve. The fast-path stays free of DB calls until the toggle is on, but flipping it with no policies in place is harmless — the engine no-ops on an empty policy set."
        actions={
          draft && (
            <Badge tone={draft.enabled ? 'success' : 'neutral'}>
              {draft.enabled ? 'engine active' : 'engine off'}
            </Badge>
          )
        }
      />
      <CardBody>
        {draft && (
          <>
            {draft.enabled && (
              <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  The engine is currently <strong>active</strong>. Pulls that
                  fail any matched policy return 403 with{' '}
                  <code className="text-xs">X-Orbital-PullGate-*</code>{' '}
                  headers. Validate the live preview below before changing
                  sync_mode or fallback_action.
                </span>
              </div>
            )}

            <label className="flex items-center gap-2 mb-4 text-sm">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) =>
                  setDraft({ ...draft, enabled: e.target.checked })
                }
              />
              <span className="font-medium">
                Enable Pull-Gate engine
              </span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label id="pg-sync-mode-label" className={LABEL_CLASS}>Sync mode</label>
                <div
                  className="space-y-2"
                  role="group"
                  aria-labelledby="pg-sync-mode-label"
                >
                  {(Object.keys(SYNC_MODE_LABELS) as SyncMode[]).map((mode) => {
                    const meta = SYNC_MODE_LABELS[mode];
                    return (
                      <label
                        key={mode}
                        className="flex items-start gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="sync_mode"
                          className="mt-1"
                          checked={draft.sync_mode === mode}
                          onChange={() =>
                            setDraft({ ...draft, sync_mode: mode })
                          }
                        />
                        <span>
                          <span className="font-medium">{meta.label}</span>
                          <span className="block text-xs text-fg-muted">
                            {meta.hint}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label htmlFor="pg-grace-period" className={LABEL_CLASS}>Grace period (hours)</label>
                <Input
                  id="pg-grace-period"
                  type="number"
                  min={0}
                  value={draft.grace_period_hours}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      grace_period_hours:
                        Number.parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
                <p className="text-xs text-fg-subtle mt-1">
                  Newly-uploaded artifacts skip the gate for this many hours
                  so the async scanner has time to land.
                </p>
              </div>
              <div>
                <label htmlFor="pg-fallback-action" className={LABEL_CLASS}>
                  Fallback when scan data is missing
                </label>
                <select
                  id="pg-fallback-action"
                  className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent text-sm"
                  value={draft.fallback_action}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      fallback_action: e.target.value as 'allow' | 'block',
                    })
                  }
                >
                  <option value="block">block (fail closed)</option>
                  <option value="allow">allow (fail open)</option>
                </select>
                <p className="text-xs text-fg-subtle mt-1">
                  Only consulted by <code>pre-scan-cache</code> mode after
                  grace.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-fg-muted">
              <span>Last update: {fmtRel(draft.updated_at)}</span>
              {draft.updated_by && (
                <span className="text-fg-subtle text-xs">
                  by {draft.updated_by}
                </span>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="primary" onClick={saveSettings} disabled={busy}>
                Save settings
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function PullGateLivePreviewCard({
  preview,
  refreshPreview,
  busy,
}: Readonly<{
  preview: { data: PullGatePreview | undefined; error: any };
  refreshPreview: () => void;
  busy: boolean;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Live preview"
        description="Runs the engine against the most-recent 200 artifacts in simulate mode. Use this to validate a draft policy set before flipping the master toggle."
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={refreshPreview}
            disabled={busy}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        }
      />
      <CardBody>
        {!preview.data && !preview.error && (
          <p className="text-sm text-fg-muted">
            Click <strong>Refresh</strong> to run the simulation.
          </p>
        )}
        {preview.error && (
          <p className="text-sm text-danger">
            Preview failed: {String(preview.error)}
          </p>
        )}
        {preview.data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <PreviewStat
                label="Sampled"
                value={preview.data.sampled}
                tone="neutral"
              />
              <PreviewStat
                label="Block"
                value={preview.data.block}
                pct={preview.data.sampled > 0
                  ? (preview.data.block / preview.data.sampled) * 100
                  : 0}
                tone="danger"
              />
              <PreviewStat
                label="Bypass"
                value={preview.data.bypass}
                pct={preview.data.sampled > 0
                  ? (preview.data.bypass / preview.data.sampled) * 100
                  : 0}
                tone="warning"
              />
              <PreviewStat
                label="Allow"
                value={preview.data.allow}
                pct={preview.data.sampled > 0
                  ? (preview.data.allow / preview.data.sampled) * 100
                  : 0}
                tone="success"
              />
            </div>

            <p className="text-sm mb-3">
              If enabled now,{' '}
              <strong className="text-danger">
                {preview.data.block_pct.toFixed(1)}%
              </strong>{' '}
              of the next pulls would be blocked.
            </p>

            {Object.keys(preview.data.by_reason).length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-fg-muted border-b border-border">
                      <th className="py-2 pr-4">Reason</th>
                      <th className="py-2 pr-4 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(preview.data.by_reason)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 20)
                      .map(([reason, count]) => (
                        <tr
                          key={reason}
                          className="border-b border-border/50"
                        >
                          <td className="py-1.5 pr-4 font-mono text-xs">
                            {reason}
                          </td>
                          <td className="py-1.5 pr-4 text-right">{count}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-fg-subtle mt-3">
              Generated {fmtRel(preview.data.generated_at)} · engine{' '}
              {preview.data.engine_enabled ? 'currently active' : 'currently off'}
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function PullGateStatsCard({
  stats,
}: Readonly<{
  stats: { data: PullGateStats | undefined; error: any };
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Block stats"
        description="Roll-up of every block decision in pull_gate_audit. The 24h sparkline plots hourly block counts; the top-policies list points at which rule fired most often in the last 30 days."
        actions={
          <Badge tone={stats.data?.engine_enabled ? 'success' : 'neutral'}>
            {stats.data?.engine_enabled ? 'engine active' : 'engine off'}
          </Badge>
        }
      />
      <CardBody>
        {stats.error && (
          <p className="text-sm text-danger">
            Stats failed: {String(stats.error)}
          </p>
        )}
        {!stats.data && !stats.error && (
          <p className="text-sm text-fg-muted">Loading…</p>
        )}
        {stats.data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatTile label="Today" value={stats.data.today} />
              <StatTile label="Last 7 days" value={stats.data.last_7d} />
              <StatTile label="Last 30 days" value={stats.data.last_30d} />
              <StatTile label="All time" value={stats.data.all_time} />
            </div>
            <div className="rounded-md border border-border bg-bg-inset/40 px-4 py-3">
              <p className="text-xs text-fg-muted uppercase tracking-wide mb-2 flex items-center gap-2">
                <Activity className="h-3.5 w-3.5" />
                24h block sparkline
              </p>
              <Sparkline values={stats.data.sparkline} />
              <div className="flex justify-between text-xs text-fg-subtle mt-1">
                <span>−24h</span>
                <span>now</span>
              </div>
            </div>
            {stats.data.blocks_by_policy.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-fg-muted uppercase tracking-wide mb-2">
                  Top blocking policies (last 30 days)
                </p>
                <ul className="space-y-1.5 text-sm">
                  {stats.data.blocks_by_policy.map((row) => (
                    <li
                      key={row.name}
                      className="flex justify-between border-b border-border/40 pb-1"
                    >
                      <span className="font-mono text-xs">{row.name}</span>
                      <span className="text-fg-muted">{row.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {stats.data.all_time === 0 && (
              <p className="text-xs text-fg-subtle mt-3">
                No blocks recorded yet — either the engine has been disabled or
                no policy has matched a real pull.
              </p>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function PullGateLiveStreamCard({
  stream,
}: Readonly<{
  stream: DecisionStreamHook;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Live decision stream"
        description="Tails every gate decision via Server-Sent Events. Allows are green, blocks red, bypasses amber. Pauses on toggle; max 50 rows kept."
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={streamStatusTone(stream.connected, stream.error)}>
              {streamStatusLabel(stream.connected, stream.error, stream.running)}
            </Badge>
            <Button
              variant="secondary"
              size="sm"
              onClick={stream.toggle}
            >
              {stream.running ? (
                <>
                  <Pause className="h-4 w-4 mr-1" /> Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" /> Resume
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={stream.clear}
              disabled={stream.events.length === 0}
            >
              Clear
            </Button>
          </div>
        }
      />
      <CardBody>
        {stream.error && (
          <p className="text-sm text-danger mb-3">
            Stream error: {stream.error}. Will reconnect automatically.
          </p>
        )}
        {stream.events.length === 0 ? (
          <p className="text-sm text-fg-muted">
            {stream.running
              ? 'Waiting for the next decision… (heartbeats keep the connection warm every 30 s.)'
              : 'Stream paused. Click Resume to start tailing.'}
          </p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bg">
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Decision</th>
                  <th className="py-2 pr-4">Path</th>
                  <th className="py-2 pr-4">Principal</th>
                  <th className="py-2 pr-4">Reason</th>
                </tr>
              </thead>
              <tbody>
                {stream.events.map((ev) => (
                  <tr
                    key={`${ev.occurred_at}-${ev.artifact_path ?? ''}-${ev.decision}`}
                    className="border-b border-border/50"
                  >
                    <td className="py-1.5 pr-4 text-xs text-fg-muted whitespace-nowrap">
                      {fmtClock(ev.occurred_at)}
                    </td>
                    <td className="py-1.5 pr-4">
                      <Badge tone={decisionBadgeTone(ev.decision)}>
                        {ev.decision === 'block' && (
                          <Ban className="h-3 w-3 mr-1 inline" />
                        )}
                        {ev.decision}
                      </Badge>
                    </td>
                    <td className="py-1.5 pr-4 font-mono text-xs break-all max-w-md">
                      {ev.artifact_path || '—'}
                    </td>
                    <td className="py-1.5 pr-4 text-xs">
                      {ev.principal_email ||
                        (ev.principal_kind === 'anonymous'
                          ? '(anonymous)'
                          : ev.principal_kind || '—')}
                    </td>
                    <td className="py-1.5 pr-4 text-xs text-fg-muted truncate max-w-md">
                      {ev.policy_name && (
                        <span className="text-fg mr-1">
                          [{ev.policy_name}]
                        </span>
                      )}
                      {ev.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-fg-subtle mt-3">
          Best-effort tail — slow connections drop events rather than block
          the artifact-serve hot path. The audit log above is the durable
          record.
        </p>
      </CardBody>
    </Card>
  );
}

function PullGatePoliciesCard({
  counts,
  editingId,
  resetPolicyForm,
  policyDraft,
  setPolicyDraft,
  submitPolicy,
  busy,
  policyRows,
  loadPolicy,
  removePolicy,
  togglePolicyEnabled,
}: Readonly<{
  counts: { repo: number; project: number; global: number; total: number };
  editingId: string | null;
  resetPolicyForm: () => void;
  policyDraft: PullGatePolicyInput;
  setPolicyDraft: (p: PullGatePolicyInput) => void;
  submitPolicy: () => void;
  busy: boolean;
  policyRows: PullGatePolicy[];
  loadPolicy: (p: PullGatePolicy) => void;
  removePolicy: (p: PullGatePolicy) => void;
  togglePolicyEnabled: (p: PullGatePolicy) => void;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Policies"
        description={`${counts.total} total — ${counts.repo} repo · ${counts.project} project · ${counts.global} global`}
        actions={
          editingId ? (
            <Button variant="ghost" size="sm" onClick={resetPolicyForm}>
              Cancel edit
            </Button>
          ) : null
        }
      />
      <CardBody>
        {/* Editor form */}
        <div className="mb-6 rounded-md border border-border bg-bg-inset/30 px-4 py-3">
          <p className="text-sm font-medium mb-3 flex items-center gap-2">
            {editingId ? (
              <>
                <Edit className="h-4 w-4" /> Edit policy
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" /> New policy
              </>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="pg-policy-name" className={LABEL_CLASS}>Name</label>
              <Input
                id="pg-policy-name"
                value={policyDraft.name}
                onChange={(e) =>
                  setPolicyDraft({ ...policyDraft, name: e.target.value })
                }
                placeholder="block-criticals-prod"
              />
            </div>
            <div>
              <label htmlFor="pg-policy-scope" className={LABEL_CLASS}>Scope</label>
              <select
                id="pg-policy-scope"
                className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent text-sm"
                value={policyDraft.scope_kind}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    scope_kind: e.target.value as PullGatePolicyInput['scope_kind'],
                    scope_id:
                      e.target.value === 'global'
                        ? ''
                        : policyDraft.scope_id,
                  })
                }
              >
                <option value="global">global (every repo)</option>
                <option value="project">project (UUID)</option>
                <option value="repo">repo (UUID)</option>
              </select>
            </div>
            {policyDraft.scope_kind !== 'global' && (
              <div className="md:col-span-2">
                <label htmlFor="pg-policy-scope-id" className={LABEL_CLASS}>
                  {policyDraft.scope_kind === 'project'
                    ? 'Project UUID'
                    : 'Repository UUID'}
                </label>
                <Input
                  id="pg-policy-scope-id"
                  value={policyDraft.scope_id ?? ''}
                  onChange={(e) =>
                    setPolicyDraft({
                      ...policyDraft,
                      scope_id: e.target.value,
                    })
                  }
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </div>
            )}
            <div>
              <label htmlFor="pg-policy-max-severity" className={LABEL_CLASS}>Max severity (block above)</label>
              <select
                id="pg-policy-max-severity"
                className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent text-sm"
                value={policyDraft.max_severity ?? ''}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    max_severity: e.target.value,
                  })
                }
              >
                <option value="">— no severity rule —</option>
                <option value="LOW">LOW</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="HIGH">HIGH</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
            </div>
            <div>
              <label htmlFor="pg-policy-max-age" className={LABEL_CLASS}>
                Max age (days, 0 = no cap)
              </label>
              <Input
                id="pg-policy-max-age"
                type="number"
                min={0}
                value={policyDraft.max_age_days ?? 0}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    max_age_days:
                      Number.parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="pg-policy-allowed-licenses" className={LABEL_CLASS}>
                Allowed licenses (comma-separated SPDX IDs; empty = no allow-list)
              </label>
              <Input
                id="pg-policy-allowed-licenses"
                value={(policyDraft.allowed_licenses ?? []).join(', ')}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    allowed_licenses: csv(e.target.value),
                  })
                }
                placeholder="Apache-2.0, MIT, BSD-3-Clause"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="pg-policy-forbidden-licenses" className={LABEL_CLASS}>
                Forbidden licenses (always block)
              </label>
              <Input
                id="pg-policy-forbidden-licenses"
                value={(policyDraft.forbidden_licenses ?? []).join(', ')}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    forbidden_licenses: csv(e.target.value),
                  })
                }
                placeholder="GPL-3.0-only, AGPL-3.0-only"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="pg-policy-bypass-patterns" className={LABEL_CLASS}>
                Bypass patterns (path globs that always pass; supports *,
                ?, **)
              </label>
              <Input
                id="pg-policy-bypass-patterns"
                value={(policyDraft.bypass_patterns ?? []).join(', ')}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    bypass_patterns: csv(e.target.value),
                  })
                }
                placeholder="docker/distroless/**, npm/internal/*"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="pg-policy-block-message" className={LABEL_CLASS}>
                Block message (appended to the 403 body when this policy
                fires)
              </label>
              <textarea
                id="pg-policy-block-message"
                className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent text-sm font-mono"
                rows={2}
                value={policyDraft.block_message_template ?? ''}
                onChange={(e) =>
                  setPolicyDraft({
                    ...policyDraft,
                    block_message_template: e.target.value,
                  })
                }
                placeholder="Contact #platform-security in Slack."
              />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={policyDraft.require_signed}
                  onChange={(e) =>
                    setPolicyDraft({
                      ...policyDraft,
                      require_signed: e.target.checked,
                    })
                  }
                />
                <span>Require Sigstore/CMS signature</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={policyDraft.require_sbom}
                  onChange={(e) =>
                    setPolicyDraft({
                      ...policyDraft,
                      require_sbom: e.target.checked,
                    })
                  }
                />
                <span>Require SBOM</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={policyDraft.enabled ?? true}
                  onChange={(e) =>
                    setPolicyDraft({
                      ...policyDraft,
                      enabled: e.target.checked,
                    })
                  }
                />
                <span>Enabled (engine evaluates this row)</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              variant="primary"
              onClick={submitPolicy}
              disabled={busy}
            >
              {editingId ? 'Save changes' : 'Create policy'}
            </Button>
            {editingId && (
              <Button
                variant="secondary"
                onClick={resetPolicyForm}
                disabled={busy}
              >
                Discard
              </Button>
            )}
          </div>
        </div>

        {/* Policies table */}
        {policyRows.length === 0 ? (
          <p className="text-sm text-fg-muted">
            No policies yet. Add one above; until then, the engine no-ops on
            every pull even if the master toggle is enabled.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Scope</th>
                  <th className="py-2 pr-4">Severity ≤</th>
                  <th className="py-2 pr-4">Age ≤</th>
                  <th className="py-2 pr-4">Flags</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {policyRows.map((p) => (
                  <tr key={p.id} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-medium">{p.name}</td>
                    <td className="py-2 pr-4">
                      <Badge tone={scopeBadgeTone(p.scope_kind)}>
                        {p.scope_kind}
                      </Badge>
                      {p.scope_id && (
                        <span className="ml-2 text-xs font-mono text-fg-subtle">
                          {p.scope_id.slice(0, 8)}…
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {p.max_severity || '—'}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {p.max_age_days ? `${p.max_age_days}d` : '—'}
                    </td>
                    <td className="py-2 pr-4 text-xs space-x-1">
                      {p.require_signed && <Badge tone="accent">signed</Badge>}
                      {p.require_sbom && <Badge tone="accent">sbom</Badge>}
                      {(p.bypass_patterns?.length ?? 0) > 0 && (
                        <Badge tone="warning">
                          {p.bypass_patterns.length} bypass
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <button
                        className="text-xs underline"
                        onClick={() => togglePolicyEnabled(p)}
                        disabled={busy}
                      >
                        {p.enabled ? (
                          <Badge tone="success">enabled</Badge>
                        ) : (
                          <Badge tone="neutral">disabled</Badge>
                        )}
                      </button>
                    </td>
                    <td className="py-2 pr-4 text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => loadPolicy(p)}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePolicy(p)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-danger" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function PullGateAuditLogCard({
  auditRows,
  auditFilter,
  setAuditFilter,
}: Readonly<{
  auditRows: PullGateAuditRow[];
  auditFilter: PullGateAction | undefined;
  setAuditFilter: (a: PullGateAction | undefined) => void;
}>) {
  return (
    <Card>
      <CardHeader
        title="Audit log"
        description="Append-only record of every gate decision (allow / block / bypass). Allow rows are written so the live preview computes against the same dataset the engine actually saw."
        actions={
          <div className="flex items-center gap-1.5 text-xs">
            <FilterChip
              active={auditFilter === undefined}
              onClick={() => setAuditFilter(undefined)}
            >
              All
            </FilterChip>
            <FilterChip
              active={auditFilter === 'allow'}
              onClick={() => setAuditFilter('allow')}
            >
              Allow
            </FilterChip>
            <FilterChip
              active={auditFilter === 'block'}
              onClick={() => setAuditFilter('block')}
            >
              Block
            </FilterChip>
            <FilterChip
              active={auditFilter === 'bypass'}
              onClick={() => setAuditFilter('bypass')}
            >
              Bypass
            </FilterChip>
          </div>
        }
      />
      <CardBody>
        {auditRows.length === 0 ? (
          <p className="text-sm text-fg-muted">
            No decisions logged yet — either the engine is disabled or no
            pulls have hit a policy in the selected window.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Decision</th>
                  <th className="py-2 pr-4">Path</th>
                  <th className="py-2 pr-4">Principal</th>
                  <th className="py-2 pr-4">Reason</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((row) => (
                  <tr key={row.id} className="border-b border-border/50">
                    <td className="py-1.5 pr-4 text-xs text-fg-muted whitespace-nowrap">
                      {fmtRel(row.occurred_at)}
                    </td>
                    <td className="py-1.5 pr-4">
                      <Badge tone={decisionBadgeTone(row.decision)}>
                        {row.decision}
                      </Badge>
                    </td>
                    <td className="py-1.5 pr-4 font-mono text-xs break-all max-w-md">
                      {row.artifact_path || '—'}
                    </td>
                    <td className="py-1.5 pr-4 text-xs">
                      {row.principal_email ||
                        (row.principal_kind === 'anonymous'
                          ? '(anonymous)'
                          : row.principal_kind)}
                    </td>
                    <td className="py-1.5 pr-4 text-xs text-fg-muted truncate max-w-md">
                      {row.policy_name && (
                        <span className="text-fg mr-1">
                          [{row.policy_name}]
                        </span>
                      )}
                      {row.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function PreviewStat({
  label,
  value,
  pct,
  tone,
}: {
  readonly label: string;
  readonly value: number;
  readonly pct?: number;
  readonly tone: 'neutral' | 'success' | 'warning' | 'danger';
}) {
  const colour = {
    neutral: 'text-fg',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[tone];
  return (
    <div className="rounded-md border border-border bg-bg-inset/40 px-3 py-2.5">
      <p className="text-xs text-fg-muted uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-semibold mt-1 ${colour}`}>
        {value}
        {pct !== undefined && (
          <span className="text-sm text-fg-subtle ml-1">
            ({pct.toFixed(1)}%)
          </span>
        )}
      </p>
      {pct === undefined && label === 'Sampled' && value === 0 && (
        <p className="text-xs text-fg-subtle mt-1">
          <CheckCircle2 className="inline h-3 w-3 mr-1" />
          no recent artifacts
        </p>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 rounded text-xs border transition-colors ${
        active
          ? 'bg-nebula/10 text-nebula border-nebula/30'
          : 'text-fg-muted border-border hover:bg-bg-hover'
      }`}
    >
      {children}
    </button>
  );
}

function StatTile({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number;
}) {
  return (
    <div className="rounded-md border border-border bg-bg-inset/40 px-3 py-2.5">
      <p className="text-xs text-fg-muted uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-semibold mt-1">
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function Sparkline({ values }: { readonly values: number[] }) {
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-1 h-16">
      {values.map((v, i) => {
        const h = Math.round((v / max) * 100);
        return (
          <div
            key={i}
            className="flex-1 bg-danger/60 rounded-sm min-h-[2px]"
            style={{ height: `${Math.max(h, v > 0 ? 8 : 2)}%` }}
            title={`${v} block${v === 1 ? '' : 's'} ${23 - i}h ago`}
          />
        );
      })}
    </div>
  );
}

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

const STREAM_MAX_EVENTS = 50;

interface DecisionStreamHook {
  events: PullGateLiveEvent[];
  connected: boolean;
  running: boolean;
  error: string | null;
  toggle: () => void;
  clear: () => void;
}

/** Owns the EventSource lifecycle for the Live Pull-Stream card.
 *  Keeps at most STREAM_MAX_EVENTS rows; auto-reconnect is handled
 *  by the browser's built-in EventSource retry on transport errors. */
function useDecisionStream(): DecisionStreamHook {
  const [events, setEvents] = useState<PullGateLiveEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [running, setRunning] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!running) {
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
      return;
    }
    const es = new EventSource(PULL_GATE_STREAM_URL, {
      withCredentials: true,
    });
    esRef.current = es;
    es.addEventListener('hello', () => {
      setConnected(true);
      setError(null);
    });
    es.addEventListener('decision', (e: MessageEvent) => {
      try {
        const ev = JSON.parse(e.data) as PullGateLiveEvent;
        setEvents((prev) => [ev, ...prev].slice(0, STREAM_MAX_EVENTS));
      } catch {
        // Ignore malformed frames; the audit table is still authoritative.
      }
    });
    es.onerror = () => {
      setConnected(false);
      setError('disconnected');
      // Browser EventSource auto-retries; nothing else to do here.
    };
    return () => {
      es.close();
      esRef.current = null;
      setConnected(false);
    };
  }, [running]);

  return {
    events,
    connected,
    running,
    error,
    toggle: () => setRunning((r) => !r),
    clear: () => setEvents([]),
  };
}
